import { CheckCircle } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { Bet, BettingSlipWithMonitoringInfo } from '../@types/api';
import BetBuilderBadge from '../components/atoms/BetBuilderBadge';
import { BettingSlipFromIcon } from '../components/icons';
import TicketDetailsInfoItem from '../components/atoms/TicketDetailsInfoItem';
import IconRenderer from '../components/atoms/IconRenderer';
import { formatScore } from './index';
import { isBetBuilderRow } from './ticketMonitoring';
import { createColumn, getBetStatusColor } from '../utils';

type CreateTicketDetailsColumnsOptions = {
  reofferedFromTicketDetails?: BettingSlipWithMonitoringInfo;
};

export const createTicketDetailsColumns = ({
  reofferedFromTicketDetails,
}: CreateTicketDetailsColumnsOptions): GridColDef[] => [
  createColumn('id', 'ID', {
    sortable: false,
  }),
  createColumn('startDate', 'Date and Time', {
    valueGetter: (value, row, column, apiRef) =>
      row.event.startDate ? dayjs(row.event.startDate).format('DD-MM-YYYY HH:mm') : '',
    alwaysVisible: true,
    sortable: false,
    flex: 1.5,
  }),
  createColumn('competitionName', 'Competition', {
    valueGetter: (value, row, column, apiRef) => row.competitionName,
    sortable: false,
    flex: 1.5,
  }),
  createColumn('tournamentName', 'Tournament', {
    valueGetter: (value, row, column, apiRef) => row.tournamentName,
    sortable: false,
    flex: 3.75,
  }),
  createColumn('icon', 'Icon', {
    renderCell: (params: GridRenderCellParams<Bet>) => <IconRenderer name={params.row.icon} />,
    sortable: false,
    alwaysVisible: true,
    flex: 0.75,
  }),
  createColumn('from', 'From', {
    renderCell: (params: GridRenderCellParams<Bet>) => {
      const from = params.row.isLive ? 'In-play' : 'Pre-match';
      return <BettingSlipFromIcon from={from} />;
    },
    sortable: false,
    flex: 0.75,
  }),
  createColumn('banker', 'Banker', {
    renderCell: (params: GridRenderCellParams<Bet>) => (params.row.banker ? <CheckCircle color='success' /> : ''),
    sortable: false,
    flex: 0.75,
  }),
  createColumn('marketCombined', 'Combined', {
    renderCell: (params: GridRenderCellParams<Bet>) =>
      params.row.marketCombined ? <CheckCircle color='success' /> : '',
    sortable: false,
    flex: 0.75,
  }),
  createColumn('event', 'Event', {
    valueGetter: (value, row, column, apiRef) => row.event.name,
    alwaysVisible: true,
    sortable: false,
    flex: 3,
  }),
  createColumn('market', 'Market', {
    valueGetter: (value, row, column, apiRef) => row.market.name,
    alwaysVisible: true,
    sortable: false,
    flex: 2.5,
  }),
  createColumn('specialValue', 'SV', {
    valueGetter: (_value, row, _column, _apiRef) => row.outcome.properties?.specialValue,
    sortable: false,
  }),
  createColumn('outcomeShortName', 'Outcome Short Name', {
    valueGetter: (value, row, column, apiRef) => row.outcome.shortName,
    sortable: false,
    flex: 2,
  }),
  createColumn('outcomeName', 'Outcome Name', {
    valueGetter: (value, row, column, apiRef) => row.outcome.name,
    sortable: false,
    flex: 2,
  }),
  createColumn('isWays', 'Ways', {
    renderCell: (params: GridRenderCellParams<Bet>) => (params.row.ways ? <CheckCircle color='success' /> : ''),
    sortable: false,
    flex: 0.75,
  }),
  createColumn('odds', 'Odds', {
    sortable: false,
    alwaysVisible: true,
    renderCell: (params: GridRenderCellParams<Bet>) => {
      if (isBetBuilderRow(params.row)) return <Typography variant='body2'>{params.value}</Typography>;

      const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
      const reofferedTicketBet = reofferedFromTicketDetails?.bets[rowIndex];

      return <TicketDetailsInfoItem oldValue={reofferedTicketBet?.odds} newValue={params.value} textVariant='body2' />;
    },
  }),
  createColumn('status', 'Status', {
    renderCell: (params: GridRenderCellParams<Bet>) => (
      <Typography variant='h5' sx={{ color: getBetStatusColor(params.row.status) }}>
        {startCase(params.value)}
      </Typography>
    ),
    sortable: false,
  }),
  createColumn('result', 'Result', {
    valueGetter: (value, row, column, apiRef) => formatScore(row.mainEventPartScore),
    sortable: false,
  }),
  createColumn('betBuilder', 'Bet Builder', {
    renderCell: (params: GridRenderCellParams<Bet>) => <BetBuilderBadge enabled={isBetBuilderRow(params.row)} />,
    sortable: false,
    flex: 0.8,
  }),
];
